import { layout, margins } from '$tablet-app/src/app/theme';
import { Button } from '@akin-home/buttons/native';
import { PasswordInput } from '@akin-home/forms/native';
import { colors } from '@akin-home/theme';
import { Auth } from 'aws-amplify';
import produce from 'immer';
import React, { useCallback, useReducer } from 'react';
import { StyleSheet, View } from 'react-native';
import { ButtonGroup } from 'react-native-elements';
import styled from 'styled-components/native';
import { z } from 'zod';
import Dialog from '../Dialog';
import FieldLabel from '../Form/FieldLabel';
import { KeyboardAvoid } from '../KeyboardAvoid';
import { PinGrid } from '../PinGrid';
import { Text } from '../Text/Text';

interface Props {
  title?: string;
  current_password_label?: string;
  password_mode?: PasswordMode;
  onClose: () => void;
  onChangeSuccess: () => void;
}

type PasswordMode = 'pin' | 'password';
type TextFieldName = 'currentPassword' | 'newPassword' | 'confirmNewPassword';

interface State {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
  pin: string;
  password_mode?: PasswordMode;
  error?: string;
}

type Action =
  | {
      type: 'set_field';
      data: { name: TextFieldName; value: string };
    }
  | { type: 'add_pin_digit'; number: string }
  | { type: 'remove_pin_digit' }
  | { type: 'clear_pin' }
  | { type: 'set_password_mode'; value: PasswordMode }
  | { type: 'set_error'; error: string };

const default_state: State = {
  currentPassword: '',
  newPassword: '',
  confirmNewPassword: '',
  pin: '',
  password_mode: 'password',
};

const changePasswordReducer = produce((state: State, action: Action) => {
  switch (action.type) {
    case 'set_field':
      state[action.data.name] = action.data.value;
      break;
    case 'set_error':
      state.error = action.error;
      break;
    case 'set_password_mode':
      state.password_mode = action.value;
      break;
    case 'add_pin_digit':
      state.pin = state.pin + action.number;
      break;
    case 'remove_pin_digit':
      state.pin = state.pin.slice(0, -1);
      break;
    case 'clear_pin':
      state.pin = '';
      break;
  }
});

const ChangePasswordModal = (props: Props) => {
  const {
    title = 'Change Password',
    current_password_label = 'Current PIN or Password',
  } = props;

  const [state, dispatch] = useReducer(changePasswordReducer, {
    ...default_state,
    ...(props.password_mode ? { password_mode: props.password_mode } : {}),
  });

  const onChangeField = (name: TextFieldName, value: string) =>
    dispatch({ type: 'set_field', data: { name, value } });

  const onSubmit = async () => {
    const res =
      state.password_mode === 'password'
        ? NewPasswordSubmission.safeParse(state)
        : NewPINSubmission.safeParse(state);

    if (!res.success) {
      dispatch({
        type: 'set_error',
        error: res.error.issues[0]?.message || 'Something went wrong',
      });
      return;
    }

    const new_password =
      state.password_mode === 'password' ? state.newPassword : state.pin;

    Auth.currentAuthenticatedUser()
      .then((user) =>
        Auth.changePassword(user, state.currentPassword, new_password)
      )
      .then(() => props.onChangeSuccess())
      .catch((error) => {
        if (error.code === 'NotAuthorizedException') {
          dispatch({
            type: 'set_error',
            error: 'Oops! That password is incorrect.',
          });
        } else {
          dispatch({
            type: 'set_error',
            error: 'That password was not complex enough',
          });
        }
      });
  };

  const onPressPasswordType = (idx: number) => {
    dispatch({
      type: 'set_password_mode',
      value: idx === 0 ? 'pin' : 'password',
    });
  };

  const onPressPinDigit = useCallback((number: string) => {
    dispatch({ type: 'add_pin_digit', number });
  }, []);

  const selected_mode_idx =
    state.password_mode === 'pin'
      ? 0
      : state.password_mode === 'password'
      ? 1
      : null;

  return (
    <KeyboardAvoid behavior="height">
      <CustomDialog visible={true} onClose={props.onClose}>
        <Body contentContainerStyle={styles.contentContainerStyle}>
          <Text preset="h3">{title}</Text>

          <View>
            <FieldLabel>{current_password_label}</FieldLabel>
            <PasswordInput
              autoFocus
              placeholder={current_password_label}
              value={state.currentPassword}
              onChangeText={(text) => onChangeField('currentPassword', text)}
            />
          </View>

          {!props.password_mode ? (
            <>
              <Text>Set a PIN or Password</Text>

              <ButtonGroup
                containerStyle={styles.button_group}
                selectedButtonStyle={styles.selected_button}
                buttons={['PIN', 'Password']}
                selectedIndex={selected_mode_idx}
                onPress={onPressPasswordType}
              />
            </>
          ) : null}

          {state.password_mode === 'pin' ? (
            <>
              <GridContainer>
                <PinGrid
                  onPressNumber={onPressPinDigit}
                  onPressClear={() => dispatch({ type: 'clear_pin' })}
                  onPressRemove={() => dispatch({ type: 'remove_pin_digit' })}
                />
              </GridContainer>

              <PasswordInput
                focusable={false}
                editable={false}
                placeholder="PIN"
                value={state.pin}
              />
            </>
          ) : state.password_mode === 'password' ? (
            <>
              <View>
                <FieldLabel>New Password</FieldLabel>
                <PasswordInput
                  secureTextEntry
                  placeholder="Your new password"
                  value={state.newPassword}
                  onChangeText={(text) => onChangeField('newPassword', text)}
                />
              </View>

              <View>
                <FieldLabel>Confirm New Password</FieldLabel>
                <PasswordInput
                  secureTextEntry
                  placeholder="Confirm your new password"
                  value={state.confirmNewPassword}
                  onChangeText={(text) =>
                    onChangeField('confirmNewPassword', text)
                  }
                />
              </View>

              {state.newPassword !== state.confirmNewPassword ? (
                <Text preset="error">Passwords do not match</Text>
              ) : null}
            </>
          ) : null}

          <Buttons>
            <Button
              style={styles.actionButton}
              size="large"
              variant="secondary"
              onPress={props.onClose}
            >
              Cancel
            </Button>

            {state.password_mode ? (
              <Button
                style={styles.actionButton}
                size="large"
                onPress={onSubmit}
              >
                Confirm
              </Button>
            ) : null}
          </Buttons>

          {state.error ? <Text preset="error">{state.error}</Text> : null}
        </Body>
      </CustomDialog>
    </KeyboardAvoid>
  );
};

const CustomDialog = styled(Dialog)`
  max-width: 95%;
`;

const Body = styled.ScrollView`
  padding: 16px;
`;

const Buttons = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: flex-end;
  gap: 16px;
  margin-top: 16px;
`;

const GridContainer = styled.View`
  flex-direction: row;
  justify-content: center;
`;

// Styles
const styles = StyleSheet.create({
  buttons: {
    ...layout.row,
    ...margins.mt6,
    gap: 16,
  },
  actionButton: {
    flex: 1,
    maxWidth: '50%',
  },
  spacer: {
    ...margins.mt4,
  },
  button_group: { minWidth: 260, borderRadius: 8 },
  selected_button: { backgroundColor: colors.primary[70] },
  contentContainerStyle: {
    gap: 10,
    flexGrow: 1,
  },
});

const CurrentPassword = z
  .string({ required_error: 'Please set your current password' })
  .min(6, { message: 'Current password not long enough' });

const NewPassword = z
  .string({ required_error: 'Please set your new password' })
  .min(6, { message: 'Password not long enough' });

const NewPIN = z
  .string({ required_error: 'Please set your new PIN' })
  .min(6, { message: 'PIN not long enough' });

const NewPasswordSubmission = z
  .object({
    currentPassword: CurrentPassword,
    newPassword: NewPassword,
    confirmNewPassword: NewPassword,
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    message: "Passwords don't match",
  });

const NewPINSubmission = z.object({
  currentPassword: CurrentPassword,
  pin: NewPIN,
});

export default ChangePasswordModal;
